import * as React from 'react';

// NOTE: all modules imported below may be imported from '@silevis/reactgrid'
import { getCellProperty } from '../TextCellTemplate/getCellProperty';
import { keyCodes } from '../TextCellTemplate/keyCodes';
import {
  inNumericKey,
  isNavigationKey,
  isCharAlphaNumeric
} from '../TextCellTemplate/keyCodeCheckings';
import { getFormattedTimeUnit } from './timeUtils';
import { getCharFromKey } from '../TextCellTemplate/getCharFromKeyCode';
import styled from 'styled-components';
import { TbClipboardText } from 'react-icons/tb';
import commonStore from '../../store/common';
import ReportCellComment from '../../pages/Report/components/ReportComments/ReportCellComment';
import { Flex, Text } from '@mantine/core';
import { getCellState, isCellEmpty } from '../../utils/cellTemplate';

// Lazy-mounts the comment icon/popover after the first hover (see SelectCellView
// in NewSelectCellTemplate for the rationale). Only used in display mode.
const DateCellView = ({ cell }: { cell: any }) => {
  const [commentMounted, setCommentMounted] = React.useState(false);
  const isEmptyValue = isCellEmpty(cell.text);
  const { isDisabled, background } = getCellState(cell, isEmptyValue);
  return (
    <ValueContainer
      style={{
        background
      }}
      className="report-field"
      onClick={e => e.stopPropagation()}
      data-key={`${cell.dataKey}-${cell.dataIndex}-${cell.tableName}-${cell.fundId}${cell.sub_asset_type ? `-${cell.sub_asset_type}` : ''}`}
      onPointerEnter={() => setCommentMounted(true)}
    >
      <div
        style={{
          color: isDisabled ? '#909296' : isEmptyValue && cell.required ? '#FF6B6B' : '#000'
        }}
        className="text"
      >
        {isEmptyValue && cell.required ? 'This field is mandatory' : cell.text}
        <Flex align="center" gap="8px">
          {cell?.difference?.is_changed && (
            <Text fz="12px" c="orange.6" style={{ whiteSpace: 'nowrap' }}>
              Changed
            </Text>
          )}

          {cell.text && cell.isStaticField && <TbClipboardText fontSize="12px" />}
        </Flex>
      </div>
      {!!cell.errorMessage && <p className="error-message">{cell.errorMessage}</p>}
      <ReportCellComment
        fundId={cell.fundId}
        fieldName={cell.dataKey}
        fundName={cell.fundName}
        tableName={cell.tableName}
        fieldOrder={cell.fieldOrder}
        cellHovered={commentMounted}
      />
    </ValueContainer>
  );
};

export class NewDateCellTemplate {
  wasEscKeyPressed = false;

  getCompatibleCell(uncertainCell: any) {
    const date = uncertainCell.date
      ? getCellProperty(uncertainCell, 'date', 'object')
      : new Date(NaN);
    const dateFormat = uncertainCell.format || new Intl.DateTimeFormat(window.navigator.language);
    const value = date.getTime();
    const text = !Number.isNaN(value) ? dateFormat.format(date) : '';
    return { ...uncertainCell, date, value, text };
  }

  /* eslint-disable @typescript-eslint/no-unused-vars */
  handleKeyDown(
    cell: any,
    keyCode: number,
    ctrl: boolean,
    _shift: boolean,
    _alt: boolean,
    key: string,
    _capsLock: boolean
  ) {
    if (!ctrl && isCharAlphaNumeric(getCharFromKey(key)))
      return {
        cell: this.getCompatibleCell({ ...cell }),
        enableEditMode: true
      };
    return {
      cell,
      enableEditMode: keyCode === keyCodes.POINTER || keyCode === keyCodes.ENTER
    };
  }

  update(cell: any, cellToMerge: any) {
    return this.getCompatibleCell({
      ...cell,
      date: new Date(cellToMerge.value)
    });
  }

  getClassName(cell: any, _isInEditMode: boolean) {
    return cell.className ? cell.className : '';
  }

  render(cell: any, isInEditMode: boolean, onCellChanged: (cell: any, commit: boolean) => void) {
    if (!isInEditMode) {
      return <DateCellView cell={cell} />;
    }

    if (!cell.date) {
      return `"cell.date" is not initialized with a date value`;
    }

    const year = getFormattedTimeUnit(cell.date.getFullYear());
    const month = getFormattedTimeUnit(cell.date.getMonth() + 1);
    const day = getFormattedTimeUnit(cell.date.getDate());

    return (
      <StyledInput
        className="rg-input"
        ref={input => {
          if (input) input.focus();
        }}
        style={{
          fontSize: '12px',
          lineHeight: '17px'
        }}
        type="date"
        disabled={
          (!cell.isStaticTemplate && cell.isStaticField) || commonStore.isViewer || cell.nonEditable
        }
        defaultValue={`${year}-${month}-${day}`}
        onChange={e => {
          if (e.currentTarget.value) {
            const [year, month, day] = e.currentTarget.value.split('-').map(v => parseInt(v));
            onCellChanged(
              this.getCompatibleCell({
                ...cell,
                date: new Date(year, month - 1, day)
              }),
              false
            );
          }
        }}
        onBlur={e => {
          if (e.currentTarget.value) {
            const [year, month, day] = e.currentTarget.value.split('-').map(v => parseInt(v));
            onCellChanged(
              this.getCompatibleCell({
                ...cell,
                date: new Date(year, month - 1, day)
              }),
              !this.wasEscKeyPressed
            );
            this.wasEscKeyPressed = false;
          }
        }}
        onKeyDown={e => {
          if (
            inNumericKey(e.keyCode) ||
            isNavigationKey(e.keyCode) ||
            e.keyCode === keyCodes.COMMA ||
            e.keyCode === keyCodes.PERIOD ||
            ((e.ctrlKey || e.metaKey) && e.keyCode === keyCodes.KEY_A)
          )
            e.stopPropagation();
          if (
            !inNumericKey(e.keyCode) &&
            !isNavigationKey(e.keyCode) &&
            e.keyCode !== keyCodes.COMMA &&
            e.keyCode !== keyCodes.PERIOD
          )
            e.preventDefault();
          if (e.keyCode === keyCodes.ESCAPE) this.wasEscKeyPressed = true;
        }}
        max="9999-12-31"
        onCopy={e => e.stopPropagation()}
        onCut={e => e.stopPropagation()}
        onPaste={e => e.stopPropagation()}
        onPointerDown={e => e.stopPropagation()}
      />
    );
  }
}

const ValueContainer = styled.div`
  padding: 6px 8px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:hover {
    .cell-comment-container {
      visibility: visible;
    }
  }

  .text {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: #000;
    font-size: 12px;
    line-height: 17px;
  }
  .error-message {
    color: #ff6b6b;
    font-size: 12px;
    line-height: 17px;
  }
`;

const StyledInput = styled.input`
  &:disabled {
    color: #909296 !important;
  }
`;
